/**
 * UsageDashboard Component
 *
 * Main container component for the Token Usage Dashboard.
 * Orchestrates data fetching and displays all child components:
 * - UsageSummaryCards (top metrics)
 * - UsageTrendChart (usage over time)
 * - UsageByModelChart, UsageByProviderChart (side by side)
 * - UsageBySourceChart (smaller)
 * - UsageByProjectChart (hierarchical breakdown by project) - AC-622
 * - UsageByAgentTable (hierarchical breakdown by agent) - AC-622
 * - UsageByWorkflowTable (full width)
 * - HierarchyBreadcrumb (drill-down navigation) - AC-622
 * - BudgetConfigPanel (collapsible sidebar)
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Technical Design Reference: docs/technical/TD-AC-622-hierarchical-cost-breakdown.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-612 (Frontend: UsageDashboard container)
 * - AC-613 (Wire into SettingsPage)
 * - AC-622 (Epic: Hierarchical Cost Breakdown)
 * - AC-632 (Wiring: Integrate hierarchical components into UsageDashboard)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw, AlertCircle, Settings2, ChevronDown, ChevronUp } from 'lucide-react';
import Tooltip from '../common/Tooltip';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import type {
  TokenUsageTimePeriod,
  TokenUsageSummary,
  TokenUsageByModel,
  TokenUsageByProvider,
  TokenUsageBySource,
  TokenUsageByWorkflow,
  TokenUsageTrend,
  UserBudget,
  BudgetConfig,
  UsageQueryParams,
  // Hierarchical cost breakdown types (AC-622)
  TokenUsageByProject,
  TokenUsageByAgent,
  BreadcrumbLevel,
  HierarchicalUsageQueryParams,
} from '../../types/tokenUsage';

import UsageSummaryCards from './UsageSummaryCards';
import UsageTrendChart from './UsageTrendChart';
import UsageByModelChart from './UsageByModelChart';
import UsageByProviderChart from './UsageByProviderChart';
import UsageBySourceChart from './UsageBySourceChart';
import UsageByWorkflowTable from './UsageByWorkflowTable';
import BudgetConfigPanel from './BudgetConfigPanel';
// Hierarchical cost breakdown components (AC-622)
import HierarchyBreadcrumb from './HierarchyBreadcrumb';
import UsageByProjectChart from './UsageByProjectChart';
import UsageByAgentTable from './UsageByAgentTable';

/**
 * Period options for the time period selector.
 */
const PERIOD_OPTIONS: { value: TokenUsageTimePeriod; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_year', label: 'This Year' },
  { value: 'all_time', label: 'All Time' },
];

/**
 * UsageDashboard component props.
 */
export interface UsageDashboardProps {
  /** Optional CSS class name */
  className?: string;
  /** When provided, fetches data for this user via admin endpoints */
  userId?: number;
  /** When true, hides budget config toggle (admin viewing another user's data) */
  readOnly?: boolean;
  /** Override the default "Token Usage" header */
  headerTitle?: string;
  /** Override the default subtitle */
  headerSubtitle?: string;
}

/**
 * Dashboard data state structure.
 */
interface DashboardData {
  summary: TokenUsageSummary | null;
  byModel: TokenUsageByModel[] | null;
  byProvider: TokenUsageByProvider[] | null;
  bySource: TokenUsageBySource[] | null;
  byWorkflow: TokenUsageByWorkflow[] | null;
  trends: TokenUsageTrend | null;
  budget: UserBudget | null;
  // Hierarchical cost breakdown data (AC-622)
  byProject: TokenUsageByProject[] | null;
  byAgent: TokenUsageByAgent[] | null;
}

/**
 * Loading state for individual sections.
 */
interface LoadingState {
  summary: boolean;
  byModel: boolean;
  byProvider: boolean;
  bySource: boolean;
  byWorkflow: boolean;
  trends: boolean;
  budget: boolean;
  // Hierarchical cost breakdown loading states (AC-622)
  byProject: boolean;
  byAgent: boolean;
}

/**
 * Error state for individual sections.
 */
interface ErrorState {
  summary: string | null;
  byModel: string | null;
  byProvider: string | null;
  bySource: string | null;
  byWorkflow: string | null;
  trends: string | null;
  budget: string | null;
  // Hierarchical cost breakdown error states (AC-622)
  byProject: string | null;
  byAgent: string | null;
}

/**
 * UsageDashboard container component.
 */
const UsageDashboard: React.FC<UsageDashboardProps> = ({
  className = '',
  userId,
  readOnly = false,
  headerTitle,
  headerSubtitle,
}) => {
  // Period selection state
  const [selectedPeriod, setSelectedPeriod] = useState<TokenUsageTimePeriod>('this_month');

  // Budget panel visibility
  const [showBudgetConfig, setShowBudgetConfig] = useState<boolean>(false);

  // Export state
  const [isExporting, setIsExporting] = useState<boolean>(false);

  // Hierarchy drill-down state (AC-622)
  const [hierarchyLevels, setHierarchyLevels] = useState<BreadcrumbLevel[]>([
    { type: 'all', id: null, name: 'All Usage' }
  ]);

  // Data state
  const [data, setData] = useState<DashboardData>({
    summary: null,
    byModel: null,
    byProvider: null,
    bySource: null,
    byWorkflow: null,
    trends: null,
    budget: null,
    byProject: null,
    byAgent: null,
  });

  // Loading state
  const [loading, setLoading] = useState<LoadingState>({
    summary: true,
    byModel: true,
    byProvider: true,
    bySource: true,
    byWorkflow: true,
    trends: true,
    budget: true,
    byProject: true,
    byAgent: true,
  });

  // Error state
  const [errors, setErrors] = useState<ErrorState>({
    summary: null,
    byModel: null,
    byProvider: null,
    bySource: null,
    byWorkflow: null,
    trends: null,
    budget: null,
    byProject: null,
    byAgent: null,
  });

  /**
   * Build query params from selected period.
   */
  const buildQueryParams = useCallback((): UsageQueryParams => {
    return { period: selectedPeriod };
  }, [selectedPeriod]);

  /**
   * Build hierarchical query params including project_id/orchestration_id filters.
   */
  const buildHierarchicalParams = useCallback((): HierarchicalUsageQueryParams => {
    const params: HierarchicalUsageQueryParams = { period: selectedPeriod };

    // Add project_id filter if drilled into a project
    const operationLevel = hierarchyLevels.find(l => l.type === 'project');
    if (operationLevel && operationLevel.id !== null) {
      params.project_id = operationLevel.id;
    }

    // Add orchestration_id filter if drilled into a workflow
    const workflowLevel = hierarchyLevels.find(l => l.type === 'orchestration');
    if (workflowLevel && workflowLevel.id !== null) {
      params.orchestration_id = workflowLevel.id;
    }

    return params;
  }, [selectedPeriod, hierarchyLevels]);

  /**
   * Fetch all usage data in parallel.
   */
  const fetchAllData = useCallback(async () => {
    const params = buildQueryParams();
    const hierarchicalParams = buildHierarchicalParams();

    // Reset loading and error states
    setLoading({
      summary: true,
      byModel: true,
      byProvider: true,
      bySource: true,
      byWorkflow: true,
      trends: true,
      budget: true,
      byProject: true,
      byAgent: true,
    });
    setErrors({
      summary: null,
      byModel: null,
      byProvider: null,
      bySource: null,
      byWorkflow: null,
      trends: null,
      budget: null,
      byProject: null,
      byAgent: null,
    });

    // Fetch all data in parallel with individual error handling
    // When userId is provided (admin view), use admin endpoints
    const [
      summaryResult,
      byModelResult,
      byProviderResult,
      bySourceResult,
      byWorkflowResult,
      trendsResult,
      budgetResult,
      byProjectResult,
      byAgentResult,
    ] = await Promise.allSettled([
      userId ? apiService.getAdminUserUsageSummary(userId, params) : apiService.getUsageSummary(params),
      userId ? apiService.getAdminUserUsageByModel(userId, params) : apiService.getUsageByModel(params),
      userId ? apiService.getAdminUserUsageByProvider(userId, params) : apiService.getUsageByProvider(params),
      userId ? apiService.getAdminUserUsageBySource(userId, params) : apiService.getUsageBySource(params),
      userId ? apiService.getAdminUserUsageByWorkflow(userId, hierarchicalParams) : apiService.getUsageByWorkflow(hierarchicalParams),
      userId ? apiService.getAdminUserUsageTrends(userId, params) : apiService.getUsageTrends(params),
      userId ? Promise.reject('skip') : apiService.getBudget(),
      userId ? apiService.getAdminUserUsageByProject(userId, params) : apiService.getUsageByProject(params),
      userId ? apiService.getAdminUserUsageByAgent(userId, hierarchicalParams) : apiService.getUsageByAgent(hierarchicalParams),
    ]);

    // Process summary result
    if (summaryResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, summary: summaryResult.value }));
    } else {
      setErrors((prev) => ({ ...prev, summary: 'Failed to load summary data' }));
    }
    setLoading((prev) => ({ ...prev, summary: false }));

    // Process by-model result
    if (byModelResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, byModel: byModelResult.value.models }));
    } else {
      setErrors((prev) => ({ ...prev, byModel: 'Failed to load model data' }));
    }
    setLoading((prev) => ({ ...prev, byModel: false }));

    // Process by-provider result
    if (byProviderResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, byProvider: byProviderResult.value.providers }));
    } else {
      setErrors((prev) => ({ ...prev, byProvider: 'Failed to load provider data' }));
    }
    setLoading((prev) => ({ ...prev, byProvider: false }));

    // Process by-source result
    if (bySourceResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, bySource: bySourceResult.value.sources }));
    } else {
      setErrors((prev) => ({ ...prev, bySource: 'Failed to load source data' }));
    }
    setLoading((prev) => ({ ...prev, bySource: false }));

    // Process by-workflow result
    if (byWorkflowResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, byWorkflow: byWorkflowResult.value.orchestrations }));
    } else {
      setErrors((prev) => ({ ...prev, byWorkflow: 'Failed to load workflow data' }));
    }
    setLoading((prev) => ({ ...prev, byWorkflow: false }));

    // Process trends result
    if (trendsResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, trends: trendsResult.value }));
    } else {
      setErrors((prev) => ({ ...prev, trends: 'Failed to load trend data' }));
    }
    setLoading((prev) => ({ ...prev, trends: false }));

    // Process budget result (404 is OK - means no budget configured)
    if (budgetResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, budget: budgetResult.value }));
    } else {
      // Budget not found is not an error - user just doesn't have a budget configured
      setData((prev) => ({ ...prev, budget: null }));
    }
    setLoading((prev) => ({ ...prev, budget: false }));

    // Process by-project result (AC-622)
    if (byProjectResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, byProject: byProjectResult.value.projects }));
    } else {
      setErrors((prev) => ({ ...prev, byProject: 'Failed to load project data' }));
    }
    setLoading((prev) => ({ ...prev, byProject: false }));

    // Process by-agent result (AC-622)
    if (byAgentResult.status === 'fulfilled') {
      setData((prev) => ({ ...prev, byAgent: byAgentResult.value.agents }));
    } else {
      setErrors((prev) => ({ ...prev, byAgent: 'Failed to load agent data' }));
    }
    setLoading((prev) => ({ ...prev, byAgent: false }));
  }, [buildQueryParams, buildHierarchicalParams, userId]);

  // Fetch data on mount and when period changes
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  /**
   * Handle CSV export.
   */
  const handleExport = async () => {
    setIsExporting(true);
    try {
      const params = buildQueryParams();
      const blob = userId
        ? await apiService.getAdminUserUsageExport(userId, params)
        : await apiService.exportUsageCsv(params);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `token-usage-${selectedPeriod}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Handle budget save.
   */
  const handleBudgetSave = async (config: BudgetConfig): Promise<void> => {
    if (data.budget) {
      // Update existing budget
      const updated = await apiService.updateBudget(config);
      setData((prev) => ({ ...prev, budget: updated }));
    } else {
      // Create new budget
      const created = await apiService.createBudget(config);
      setData((prev) => ({ ...prev, budget: created }));
    }
    // Refresh summary to get updated budget status
    try {
      const summary = await apiService.getUsageSummary(buildQueryParams());
      setData((prev) => ({ ...prev, summary }));
    } catch (err) {
      // Non-critical error
    }
  };

  /**
   * Handle budget delete.
   */
  const handleBudgetDelete = async (): Promise<void> => {
    await apiService.deleteBudget();
    setData((prev) => ({ ...prev, budget: null }));
    // Refresh summary to remove budget status
    try {
      const summary = await apiService.getUsageSummary(buildQueryParams());
      setData((prev) => ({ ...prev, summary }));
    } catch (err) {
      // Non-critical error
    }
  };

  // ==========================================================================
  // HIERARCHICAL DRILL-DOWN HANDLERS (AC-622, AC-632)
  // ==========================================================================

  /**
   * Handle drill-down into a project.
   * Updates hierarchy and refetches agent data filtered by project_id.
   */
  const handleProjectDrillDown = useCallback(async (project: TokenUsageByProject) => {
    // Cannot drill into unattributed (null project_id)
    if (project.project_id === null) return;

    // Update hierarchy to include the project
    const newLevels: BreadcrumbLevel[] = [
      { type: 'all', id: null, name: 'All Usage' },
      { type: 'project', id: project.project_id, name: project.project_name }
    ];
    setHierarchyLevels(newLevels);

    // Refetch workflow and agent data with project filter
    setLoading((prev) => ({ ...prev, byWorkflow: true, byAgent: true }));
    setErrors((prev) => ({ ...prev, byWorkflow: null, byAgent: null }));

    const queryParams = {
      period: selectedPeriod,
      project_id: project.project_id,
    };

    try {
      // Fetch both workflow and agent data in parallel
      const [orchResult, agentResult] = await Promise.all([
        userId ? apiService.getAdminUserUsageByWorkflow(userId, queryParams) : apiService.getUsageByWorkflow(queryParams),
        userId ? apiService.getAdminUserUsageByAgent(userId, queryParams) : apiService.getUsageByAgent(queryParams),
      ]);
      setData((prev) => ({
        ...prev,
        byWorkflow: orchResult.orchestrations,
        byAgent: agentResult.agents,
      }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        byWorkflow: 'Failed to load workflow data',
        byAgent: 'Failed to load agent data',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, byWorkflow: false, byAgent: false }));
    }
  }, [selectedPeriod, userId]);

  /**
   * Handle drill-down into a workflow from the workflow table.
   * Updates hierarchy and refetches agent data filtered by orchestration_id.
   */
  const handleWorkflowDrillDown = useCallback(async (orchestration: TokenUsageByWorkflow) => {
    // Cannot drill into unattributed (null orchestration_id)
    if (orchestration.orchestration_id === null) return;

    // Preserve existing project level if present
    const operationLevel = hierarchyLevels.find(l => l.type === 'project');

    const newLevels: BreadcrumbLevel[] = [
      { type: 'all', id: null, name: 'All Usage' },
    ];

    if (operationLevel) {
      newLevels.push(operationLevel);
    }

    newLevels.push({
      type: 'orchestration',
      id: orchestration.orchestration_id,
      name: orchestration.orchestration_name
    });

    setHierarchyLevels(newLevels);

    // Refetch agent data with workflow filter
    setLoading((prev) => ({ ...prev, byAgent: true }));
    setErrors((prev) => ({ ...prev, byAgent: null }));
    try {
      const agentParams: HierarchicalUsageQueryParams = {
        period: selectedPeriod,
        orchestration_id: orchestration.orchestration_id,
      };
      // Include project_id if we have it
      if (operationLevel && operationLevel.id !== null) {
        agentParams.project_id = operationLevel.id;
      }
      const result = userId
        ? await apiService.getAdminUserUsageByAgent(userId, agentParams)
        : await apiService.getUsageByAgent(agentParams);
      setData((prev) => ({ ...prev, byAgent: result.agents }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, byAgent: 'Failed to load agent data' }));
    } finally {
      setLoading((prev) => ({ ...prev, byAgent: false }));
    }
  }, [selectedPeriod, hierarchyLevels, userId]);

  /**
   * Handle breadcrumb navigation - navigate up the hierarchy.
   */
  const handleBreadcrumbNavigate = useCallback(async (level: BreadcrumbLevel, index: number) => {
    // Slice to the clicked level (inclusive)
    const newLevels = hierarchyLevels.slice(0, index + 1);
    setHierarchyLevels(newLevels);

    // Refetch workflow and agent data with appropriate filters
    setLoading((prev) => ({ ...prev, byWorkflow: true, byAgent: true }));
    setErrors((prev) => ({ ...prev, byWorkflow: null, byAgent: null }));

    try {
      const queryParams: HierarchicalUsageQueryParams = {
        period: selectedPeriod,
      };

      // Apply project filter if we're at or below project level
      const operationLevel = newLevels.find(l => l.type === 'project');
      if (operationLevel && operationLevel.id !== null) {
        queryParams.project_id = operationLevel.id;
      }

      // Apply workflow filter if we're at workflow level
      const workflowLevel = newLevels.find(l => l.type === 'orchestration');
      if (workflowLevel && workflowLevel.id !== null) {
        queryParams.orchestration_id = workflowLevel.id;
      }

      // Fetch both workflow and agent data in parallel
      const [orchResult, agentResult] = await Promise.all([
        userId ? apiService.getAdminUserUsageByWorkflow(userId, queryParams) : apiService.getUsageByWorkflow(queryParams),
        userId ? apiService.getAdminUserUsageByAgent(userId, queryParams) : apiService.getUsageByAgent(queryParams),
      ]);
      setData((prev) => ({
        ...prev,
        byWorkflow: orchResult.orchestrations,
        byAgent: agentResult.agents,
      }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        byWorkflow: 'Failed to load workflow data',
        byAgent: 'Failed to load agent data',
      }));
    } finally {
      setLoading((prev) => ({ ...prev, byWorkflow: false, byAgent: false }));
    }
  }, [selectedPeriod, hierarchyLevels, userId]);

  /**
   * Reset hierarchy to top level when period changes.
   */
  useEffect(() => {
    setHierarchyLevels([{ type: 'all', id: null, name: 'All Usage' }]);
  }, [selectedPeriod]);

  /**
   * Check if any data is still loading.
   */
  const isAnyLoading = Object.values(loading).some(Boolean);

  /**
   * Build filter context string for display in child components.
   * Shows the project name when drilling into a project,
   * or "Project > Workflow" when drilling into a workflow.
   */
  const getWorkflowFilterContext = (): string | undefined => {
    const operationLevel = hierarchyLevels.find(l => l.type === 'project');
    if (operationLevel) {
      return operationLevel.name;
    }
    return undefined;
  };

  const getAgentFilterContext = (): string | undefined => {
    const operationLevel = hierarchyLevels.find(l => l.type === 'project');
    const workflowLevel = hierarchyLevels.find(l => l.type === 'orchestration');

    if (workflowLevel && operationLevel) {
      return `${operationLevel.name} > ${workflowLevel.name}`;
    }
    if (workflowLevel) {
      return workflowLevel.name;
    }
    if (operationLevel) {
      return operationLevel.name;
    }
    return undefined;
  };

  return (
    <div
      data-testid="usage-dashboard"
      className={`space-y-6 ${className}`}
    >
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-warm-white">{headerTitle || 'Token Usage'}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {headerSubtitle || 'Monitor your AI token consumption and costs'}
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* Period Selector */}
          <CustomSelect
            value={selectedPeriod}
            onChange={(value) => setSelectedPeriod(value as TokenUsageTimePeriod)}
            options={PERIOD_OPTIONS.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            aria-label="Period"
          />

          {/* Refresh Button */}
          <Tooltip content="Refresh data">
          <button
            data-testid="refresh-button"
            onClick={fetchAllData}
            disabled={isAnyLoading}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-warm-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${isAnyLoading ? 'animate-spin' : ''}`} />
          </button>
          </Tooltip>

          {/* Export Button */}
          <button
            data-testid="export-button"
            onClick={handleExport}
            disabled={isExporting || isAnyLoading}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>

          {/* Budget Config Toggle (hidden in read-only/admin mode) */}
          {!readOnly && (
          <button
            data-testid="budget-config-toggle"
            onClick={() => setShowBudgetConfig(!showBudgetConfig)}
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              showBudgetConfig
                ? 'bg-blue-100 dark:bg-primary-900/30 text-blue-700 dark:text-blue-300 border border-primary-300 dark:border-primary-700'
                : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Settings2 className="w-4 h-4" />
            Budget
            {showBudgetConfig ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          )}
        </div>
      </div>

      {/* Budget Config Panel (collapsible) */}
      {showBudgetConfig && (
        <div data-testid="budget-config-section">
          <BudgetConfigPanel
            budget={data.budget}
            loading={loading.budget}
            error={errors.budget}
            onSave={handleBudgetSave}
            onDelete={handleBudgetDelete}
          />
        </div>
      )}

      {/* Hierarchy Breadcrumb (AC-622) - shown when drilled down (2+ levels) */}
      <HierarchyBreadcrumb
        levels={hierarchyLevels}
        onNavigate={handleBreadcrumbNavigate}
      />

      {/* Summary Cards */}
      <UsageSummaryCards
        summary={data.summary}
        loading={loading.summary}
        error={errors.summary}
      />

      {/* Trend Chart (full width) */}
      <UsageTrendChart
        data={data.trends}
        loading={loading.trends}
        error={errors.trends}
      />

      {/* Model and Provider Charts (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UsageByModelChart
          data={data.byModel}
          loading={loading.byModel}
          error={errors.byModel}
        />
        <UsageByProviderChart
          data={data.byProvider}
          loading={loading.byProvider}
          error={errors.byProvider}
        />
      </div>

      {/* Source Chart */}
      <UsageBySourceChart
        data={data.bySource}
        loading={loading.bySource}
        error={errors.bySource}
      />

      {/* Usage by Project Chart (AC-622 - Hierarchical breakdown) */}
      <UsageByProjectChart
        data={data.byProject}
        loading={loading.byProject}
        error={errors.byProject}
        onProjectClick={handleProjectDrillDown}
      />

      {/* Workflow Table (full width) */}
      <UsageByWorkflowTable
        data={data.byWorkflow}
        loading={loading.byWorkflow}
        error={errors.byWorkflow}
        onWorkflowClick={handleWorkflowDrillDown}
        filterContext={getWorkflowFilterContext()}
      />

      {/* Usage by Agent Table (AC-622 - Hierarchical breakdown) */}
      <UsageByAgentTable
        data={data.byAgent}
        loading={loading.byAgent}
        error={errors.byAgent}
        filterContext={getAgentFilterContext()}
      />
    </div>
  );
};

export default UsageDashboard;
